import type {ReactElement} from 'react';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {EmojiClickData} from 'emoji-picker-react';
import EmojiPicker from 'emoji-picker-react';
import type {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import styles from './emoji-picker-input.module.scss';
import useOutsideClick from "@Hooks/useOutsideClick";

type EmojiPickerInputProps = {alignment?: ALIGNMENT_TYPE; iconSize?: IconSize} & (
  | {textAreaRef: React.RefObject<HTMLTextAreaElement | null>; onEmojiSelectCallback: (textWithEmoji: string) => void}
  | {onEmojiPick: (emoji: string) => void}
);

const EMOJI_PICKER_HEIGHT = 400;
const EMOJI_PICKER_WIDTH = 280;

export function EmojiPickerInput(props: EmojiPickerInputProps): ReactElement {
  const [isEmojiSelectorVisible, setIsEmojiSelectorVisible] = useState(false);
  const iconRef = useRef<HTMLElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiPickerContainerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(emojiPickerContainerRef, (): void => {
    setIsEmojiSelectorVisible(false)
  })

  const onEmojiPick = 'onEmojiPick' in props ? props.onEmojiPick : null;
  const textAreaRef = 'textAreaRef' in props ? props.textAreaRef : null;
  const onEmojiSelectCallback = 'onEmojiSelectCallback' in props ? props.onEmojiSelectCallback : null;

  const onEmojiSelected = useCallback((emojiData: EmojiClickData): void => {
    const emoji = String.fromCodePoint(parseInt(emojiData.unified, 16));
    if (onEmojiPick) {
      onEmojiPick(emoji);
      return;
    }
    if (textAreaRef?.current) {
      const textArea = textAreaRef.current;
      const {selectionStart, selectionEnd} = textArea;

      const textBefore = textArea.value.substring(0, selectionStart);
      const textAfter = textArea.value.substring(selectionEnd);

      const newCaption = `${textBefore}${emoji}${textAfter}`;

      // eslint-disable-next-line react-compiler/react-compiler
      textArea.value = newCaption;

      const newCursorPosition = selectionStart + emoji.length;
      textArea.selectionStart = textArea.selectionEnd = newCursorPosition;

      onEmojiSelectCallback?.(newCaption);
    }
  }, [onEmojiPick, textAreaRef, onEmojiSelectCallback]);

  const onEmojiSelectorClick = (): void => {
    setIsEmojiSelectorVisible(!isEmojiSelectorVisible);
  };

  const emojiPicker = useMemo((): ReactElement | null => {
    if (!isEmojiSelectorVisible) {
      return null;
    }

    return (
      <div ref={emojiPickerRef} className={`${styles.emojiSelector} ${isEmojiSelectorVisible ? styles.visible : ''}`}>
        <EmojiPicker width={EMOJI_PICKER_WIDTH} height={EMOJI_PICKER_HEIGHT} onEmojiClick={onEmojiSelected} lazyLoadEmojis/>
      </div>
    );
  }, [isEmojiSelectorVisible, onEmojiSelected]);

  return (
    <div ref={emojiPickerContainerRef} className={styles.emojiPickerInputContainer}>
      <Icon
        ref={iconRef}
        isSelected={isEmojiSelectorVisible}
        className={styles.emojiPickerIcon}
        icon="icon-sentiment-very-satisfied"
        type={IconType.GHOST}
        shape={IconShape.SQUARE}
        size={props.iconSize ?? IconSize.SIZE_ICON_20}
        onClick={onEmojiSelectorClick}
      />
      {emojiPicker}
    </div>
  );
}
